import React, { useState, useEffect } from 'react';
import { PropagateLoader } from 'react-spinners';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

import { FaAngleDown, FaFileAlt } from 'react-icons/fa';

import Paginator from '~/components/Paginator';
import PrevButton from '~/components/Paginator/PrevButton';
import NextButton from '~/components/Paginator/NextButton';
import CenterButton from '~/components/Paginator/CenterButton';
import StoreRelationshipTopicDetailModal from '~/components/Modals/StoreRelationships/TopicDetail';

import Input from '~/components/Input';

import api from '~/services/api';

import {
  Container,
  Label,
  Wrapper,
  Table,
  Th,
  Td,
  DetailBtn,
  ButtonsContent,
} from './styles';

interface ITopicsProps {
  store_relationship_id: string;
}

interface IStoreRelationshipTopic {
  id: string;
  sequence: number;
  description: string;
  store_relationship_id: string;
  user_inclusion_id: string;
  user_update_id: string;
  store_relationship: {
    code: string;
  };
  store_relationship_topic_type: {
    code: string;
    name: string;
  };
  user_inclusion: {
    name: string;
  };
  user_update: {
    name: string;
  };
  created_at: string;
  updated_at: string;
}

const Topics: React.FC<ITopicsProps> = ({ store_relationship_id }) => {
  const [page, setPage] = useState(0);
  const [pageFilter, setPageFilter] = useState(0);
  const [filter, setFilter] = useState('');
  const [filterHelper, setFilterHelper] = useState('');
  const [refresh, setRefresh] = useState(true);
  const [storeRelationshipTopics, setStoreRelationshipTopics] =
    useState<IStoreRelationshipTopic[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [storeRelationshipCodeDetail, setStoreRelationshipCodeDetail] =
    useState('');
  const [idDetail, setIdDetail] = useState('');
  const [sequenceDetail, setSequenceDetail] = useState('');
  const [typeDetail, setTypeDetail] = useState('');
  const [descriptionDetail, setDescriptionDetail] = useState('');
  const [createdAtDetail, setCreatedAtDetail] = useState(new Date());
  const [updatedAtDetail, setUpdatedAtDetail] = useState(new Date());
  const [userInclusionDetail, setUserInclusionDetail] = useState('');
  const [userUpdateDetail, setUserUpdateDetail] = useState('');

  useEffect(() => {
    if (refresh && store_relationship_id) {
      setRefresh(false);
      let pageSearch = 0;
      let foundData = false;

      if (filter !== filterHelper) {
        setPage(0);
        setPageFilter(0);
      }

      setFilterHelper(filter);

      if (filter !== '') {
        pageSearch = pageFilter;
      } else {
        pageSearch = page;
      }

      api
        .get(
          `/storerelationshiptopics/storerelationship` +
            `?store_relationship_id=${store_relationship_id}` +
            `&page=${pageSearch}` +
            `&filter=${filter}`,
        )
        .then(firstResponse => {
          if (firstResponse.data.length > 0) {
            foundData = true;
            setStoreRelationshipTopics(firstResponse.data);
            setLoading(false);
          }

          if (!foundData) {
            if (pageSearch - 1 >= 0) {
              pageSearch -= 1;
              if (filter !== '') {
                setPageFilter(pageFilter - 1);
              } else {
                setPage(page - 1);
              }
            }

            api
              .get(
                `/storerelationshiptopics/storerelationship` +
                  `?store_relationship_id=${store_relationship_id}` +
                  `&page=${pageSearch}` +
                  `&filter=${filter}`,
              )
              .then(secondResponse => {
                setStoreRelationshipTopics(secondResponse.data);
                setLoading(false);

                api
                  .get(
                    `/storerelationshiptopics/storerelationship` +
                      `?store_relationship_id=${store_relationship_id}` +
                      `&page=${pageSearch + 1}` +
                      `&filter=${filter}`,
                  )
                  .then(thirdResponse => {
                    setNextDisabled(false);
                    if (thirdResponse.data.length === 0) {
                      setNextDisabled(true);
                    }
                  });
              });
          } else {
            api
              .get(
                `/storerelationshiptopics/storerelationship` +
                  `?store_relationship_id=${store_relationship_id}` +
                  `&page=${pageSearch + 1}` +
                  `&filter=${filter}`,
              )
              .then(fourthResponse => {
                setNextDisabled(false);
                if (fourthResponse.data.length === 0) {
                  setNextDisabled(true);
                }
              });
          }
        });
    }
  }, [page, pageFilter, filter, filterHelper, refresh, store_relationship_id]);

  function handlePage(type: string): void {
    if (type === 'backward') {
      if (filter !== '') {
        setPageFilter(pageFilter - 1);
      } else {
        setPage(page - 1);
      }
    } else if (type === 'forward') {
      if (filter !== '') {
        setPageFilter(pageFilter + 1);
      } else {
        setPage(page + 1);
      }
    } else if (filter !== '') {
      setPageFilter(0);
    } else {
      setPage(0);
    }
    setRefresh(true);
  }

  function handlePrevButtonDisabled(): boolean {
    let validationPage = 0;

    if (filter !== '') {
      validationPage = pageFilter;
    } else {
      validationPage = page;
    }

    if (validationPage === 0) {
      return true;
    }

    return false;
  }

  function handleFilterChange(value: string): void {
    setFilter(value);
    setRefresh(true);
  }

  function getSequence(receivedSequence: number): React.ReactNode {
    return (
      <span style={{ marginRight: 30 }}>{receivedSequence.toString()}</span>
    );
  }

  function getDescription(receivedDescription: string): string {
    if (receivedDescription.length > 30) {
      return `${receivedDescription.substring(0, 30)}...`;
    }

    return receivedDescription;
  }

  function handleTable(): React.ReactNode {
    if (storeRelationshipTopics) {
      if (storeRelationshipTopics.length > 0) {
        return (
          <Table>
            <thead>
              <tr>
                <Th width="20%" textAlign="left">
                  Sequência
                </Th>
                <Th width="30%" textAlign="left">
                  Tipo de Tópico
                </Th>
                <Th width="40%" textAlign="left">
                  Descrição
                </Th>
                <Th width="10%" textAlign="center" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <Td colSpan={4} padding="1rem" textAlign="center">
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              )}
              {storeRelationshipTopics?.map(storeRelationshipTopic => (
                <tr key={storeRelationshipTopic.id}>
                  <Td width="20%" textAlign="right" heading="SEQUÊNCIA">
                    {getSequence(storeRelationshipTopic.sequence)}
                  </Td>
                  <Td width="30%" textAlign="left" heading="TIPO DE TÓPICO">
                    {`(${storeRelationshipTopic.store_relationship_topic_type.code}) - ` +
                      `${storeRelationshipTopic.store_relationship_topic_type.name}`}
                  </Td>
                  <Td width="40%" textAlign="left" heading="DESCRIÇÃO">
                    {getDescription(storeRelationshipTopic.description)}
                  </Td>
                  <Td
                    width="10%"
                    textAlign="center"
                    display="flex"
                    heading={' '}
                  >
                    <DetailBtn
                      onClick={() => {
                        setIsOpenDetail(true);
                        setStoreRelationshipCodeDetail(
                          storeRelationshipTopic.store_relationship.code,
                        );
                        setIdDetail(storeRelationshipTopic.id);
                        setSequenceDetail(
                          storeRelationshipTopic.sequence.toString(),
                        );
                        setTypeDetail(
                          `(${storeRelationshipTopic.store_relationship_topic_type.code})` +
                            ` - ${storeRelationshipTopic.store_relationship_topic_type.name}`,
                        );
                        setDescriptionDetail(
                          storeRelationshipTopic.description,
                        );
                        setCreatedAtDetail(
                          new Date(storeRelationshipTopic.created_at),
                        );
                        setUpdatedAtDetail(
                          new Date(storeRelationshipTopic.updated_at),
                        );
                        setUserInclusionDetail(
                          storeRelationshipTopic.user_inclusion.name,
                        );
                        setUserUpdateDetail(
                          storeRelationshipTopic.user_update.name,
                        );
                      }}
                    >
                      <FaFileAlt size={22} />
                    </DetailBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        );
      }
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th width="100%" textAlign="center" />
          </tr>
        </thead>
        <tbody>
          <tr>
            <Td width="100%" textAlign="center">
              Nenhuma informação disponível
            </Td>
          </tr>
        </tbody>
      </Table>
    );
  }

  function clearStoreRelationshipTopicDetail(): void {
    setStoreRelationshipCodeDetail('');
    setIdDetail('');
    setSequenceDetail('');
    setTypeDetail('');
    setDescriptionDetail('');
    setCreatedAtDetail(new Date());
    setUpdatedAtDetail(new Date());
    setUserInclusionDetail('');
    setUserUpdateDetail('');
  }

  if (!store_relationship_id) {
    return null;
  }

  return (
    <Container>
      <Accordion>
        <AccordionSummary expandIcon={<FaAngleDown />}>
          <Label>Tópicos</Label>
        </AccordionSummary>

        <AccordionDetails>
          <Wrapper>
            <form onSubmit={() => {}}>
              <div
                style={{
                  display: 'flex',
                  maxWidth: 680,
                }}
              >
                <Input
                  name="search"
                  labelText="Pesquisar"
                  type="text"
                  onChange={event => handleFilterChange(event.target.value)}
                />
              </div>
            </form>

            {handleTable()}

            <ButtonsContent>
              <Paginator>
                <PrevButton
                  disabled={handlePrevButtonDisabled()}
                  onClick={() => handlePage('backward')}
                />
                <CenterButton onClick={() => handlePage('reset')} />
                <NextButton
                  disabled={nextDisabled}
                  onClick={() => handlePage('forward')}
                />
              </Paginator>
            </ButtonsContent>
          </Wrapper>
        </AccordionDetails>
      </Accordion>

      <StoreRelationshipTopicDetailModal
        isOpen={isOpenDetail}
        store_relationship_code={storeRelationshipCodeDetail}
        store_relationship_topic_id={idDetail}
        sequence={sequenceDetail}
        type={typeDetail}
        description={descriptionDetail}
        created_at={createdAtDetail}
        updated_at={updatedAtDetail}
        user_inclusion={userInclusionDetail}
        user_update={userUpdateDetail}
        setIsOpen={setIsOpenDetail}
        onClose={() => {
          clearStoreRelationshipTopicDetail();
        }}
      />
    </Container>
  );
};

export default Topics;
